// src/services/apiService.ts
import axios from "axios";
import supabaseClient from "../lib/supabaseClient";

// Configure axios client with direct backend URL as fallback
// Use localhost for development, production URL as fallback
const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:4000"
    : "https://api.sendupdates.ai");

const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: 200000,
});

client.interceptors.request.use(async (config) => {
  try {
    // Increase timeout to handle session initialization delays during OAuth flows
    const sessionPromise = supabaseClient.auth.getSession();
    const timeoutPromise = new Promise(
      (_, reject) =>
        setTimeout(() => reject(new Error("Session timeout")), 5000) // Increased from 2000ms to 5000ms
    );

    const result = await Promise.race([sessionPromise, timeoutPromise]);
    const session = (result as any)?.data?.session;

    if (session?.access_token) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = `Bearer ${session.access_token}`;
    } else {
      // Try to refresh the session if no valid session
      try {
        console.log("API interceptor: No valid session, attempting refresh...");
        const refreshResult = await supabaseClient.auth.refreshSession();
        if (refreshResult.data?.session?.access_token) {
          config.headers = config.headers || {};
          config.headers[
            "Authorization"
          ] = `Bearer ${refreshResult.data.session.access_token}`;
          console.log("API interceptor: Session refreshed successfully");
        } else {
          console.log("API interceptor: Session refresh returned no session");
        }
      } catch (refreshError) {
        console.log("API interceptor: Session refresh failed:", refreshError);
      }
    }
  } catch (error) {
    console.log(
      "API interceptor: Session fetch failed or timed out, proceeding without auth:",
      error
    );
    // Proceed without authorization header - let the backend handle auth errors
  }

  return config;
});

// Helper function to get auth headers
const getAuthHeaders = async () => {
  try {
    const { data } = await supabaseClient.auth.getSession();
    if (data.session?.access_token) {
      return {
        Authorization: `Bearer ${data.session.access_token}`,
      };
    }
  } catch (error) {
    console.error("Error getting auth headers:", error);
  }
  return {};
};

export const apiService = {
  // HTTP methods using the configured client
  get: (url: string, config?: any) => client.get(url, config),
  post: (url: string, data?: any, config?: any) =>
    client.post(url, data, config),
  put: (url: string, data?: any, config?: any) => client.put(url, data, config),
  patch: (url: string, data?: any, config?: any) =>
    client.patch(url, data, config),
  delete: (url: string, config?: any) => client.delete(url, config),

  // Debug endpoints (dev-only; backend guards in prod)
  getDebugProviders: async (): Promise<{
    success: boolean;
    providers: string[];
  }> => {
    const res = await client.get("/debug/providers");
    return res.data;
  },
  getIntegrationDebug: async (
    provider: string
  ): Promise<{
    success: boolean;
    provider: string;
    auth: any;
    data: any;
    timestamp: string;
    requests: Array<{
      url: string;
      method?: string;
      headers?: Record<string, any>;
      body?: any;
      status?: number;
      statusText?: string;
      duration_ms?: number;
    }>;
  }> => {
    const res = await client.get(`/debug/integration/${provider}`);
    return res.data;
  },
  refreshIntegrationTokens: async (
    provider: string
  ): Promise<{ success: boolean; refreshed?: boolean; error?: string }> => {
    const res = await client.post(`/debug/integration/${provider}/refresh`);
    return res.data;
  },

  // Dashboard overview
  getDashboardOverview: async (): Promise<{
    success: boolean;
    data: any;
    error?: string;
  }> => {
    const res = await client.get(`/dashboard`);
    return res.data;
  },

  // LLM config endpoints
  getLlmConfig: async (): Promise<{
    success: boolean;
    data: {
      preferredProvider: "openai" | "anthropic" | null;
      hasOpenAIKey: boolean;
      hasAnthropicKey: boolean;
    };
  }> => {
    const res = await client.get("/api/llm/config");
    return res.data;
  },

  // Onboarding endpoints
  completeOnboarding: async (payload: {
    onboarding: Record<string, any>;
  }): Promise<{ success: boolean }> => {
    const res = await client.post("/user/onboarding-complete", payload);
    return res.data;
  },
  updateLlmConfig: async (payload: {
    preferredProvider?: "openai" | "anthropic";
    openaiApiKey?: string;
    anthropicApiKey?: string;
  }): Promise<{
    success: boolean;
    data: {
      preferredProvider: "openai" | "anthropic" | null;
      hasOpenAIKey: boolean;
      hasAnthropicKey: boolean;
    };
  }> => {
    const res = await client.post("/api/llm/config", payload);
    return res.data;
  },

  setPreferredProvider: async (preferredProvider: "openai" | "anthropic") => {
    const res = await client.post("/api/llm/config/provider", {
      preferredProvider,
    });
    return res.data;
  },

  /**
   * Check user onboarding status with proper error handling
   */
  checkOnboardingStatus: async (): Promise<{
    exists: boolean;
    onboardingComplete: boolean;
    hasFullName: boolean;
    role?: string;
  } | null> => {
    try {
      const authHeaders = await getAuthHeaders();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };

      // Only add Authorization header if it exists
      if (authHeaders.Authorization) {
        headers.Authorization = authHeaders.Authorization;
      }

      console.log("Making API call to onboarding-status endpoint");
      console.log("Using API base URL:", API_BASE_URL);

      // Use correct backend route path (no /api prefix)
      const apiUrl = `${API_BASE_URL}/user/onboarding-status`;
      console.log("Full API URL:", apiUrl);

      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      console.log("API response status:", response.status, response.statusText);
      console.log(
        "API response headers:",
        Object.fromEntries(response.headers.entries())
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.log(
            "User not authenticated - this is expected for logged out users"
          );
          return null;
        }

        console.error(
          "Failed to check onboarding status:",
          response.status,
          response.statusText
        );

        // Try to get response text for debugging
        const responseText = await response.text();
        console.error("Response body:", responseText);
        return null;
      }

      // Check if response is actually JSON before parsing
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        console.error("API returned non-JSON response:", contentType);
        const responseText = await response.text();
        console.error("Non-JSON response body:", responseText);
        return null;
      }

      const result = await response.json();
      console.log("API response data:", result);
      return result;
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      return null;
    }
  },

  // Notifications
  getNotifications: async (params?: {
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<{
    success: boolean;
    data: {
      notifications: Array<{
        id: string;
        type: string;
        message: string;
        read: boolean;
        created_at: string;
      }>;
      unread_count: number;
    };
  }> => {
    const res = await client.get("/notifications", { params });
    return res.data;
  },

  // Email monitoring endpoints
  getEmailSummary: async (): Promise<{
    success: boolean;
    data: {
      total_updates: number;
      scheduled_updates: number;
      total_recipients_scheduled: number;
      total_recipients_sent: number;
      total_delivered: number;
      total_opened: number;
      total_failed: number;
      open_rate: number;
      last_sent_at: string | null;
      active_workflows: number;
      scheduled_workflow_jobs_pending: number;
      total_send_sessions: number;
      completed_send_sessions: number;
      failed_send_sessions: number;
      total_send_results: number;
      total_send_results_success: number;
      total_send_results_failed: number;
      last_send_session_at: string | null;
      total_workflow_executions: number;
      last_workflow_execution_at: string | null;
    };
  }> => {
    const res = await client.get("/emailUpdates/summary");
    return res.data;
  },

  getEmailHistory: async (params?: {
    page?: number;
    limit?: number;
  }): Promise<{
    success: boolean;
    data: {
      items: Array<{
        email_update_id: string;
        subject: string | null;
        last_sent_at: string | null;
        counts: {
          sent: number;
          delivered: number;
          opened: number;
          failed: number;
        };
      }>;
      total: number;
      page: number;
      limit: number;
    };
  }> => {
    const res = await client.get("/emailUpdates/history", { params });
    return res.data;
  },

  getScheduledEmails: async (): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      type: "email_update" | "workflow";
      name: string;
      subject: string | null;
      scheduled_at: string;
      recipient_count: number;
      workflow_id?: string;
    }>;
  }> => {
    const res = await client.get("/emailUpdates/scheduled");
    return res.data;
  },

  getSendSessionDetails: async (
    sessionId: string
  ): Promise<{
    success: boolean;
    data: {
      session: any;
      results: Array<{
        send_session_id: string;
        contact_id: string;
        email: string;
        contact_name?: string | null;
        success: boolean;
        error_message?: string | null;
        error_code?: string | null;
        sent_at: string;
        opened_at?: string | null;
        retry_count?: number;
        last_retry_at?: string | null;
      }>;
      counts: {
        sent: number;
        delivered: number;
        opened: number;
        failed: number;
      };
      last_sent_at: string | null;
    };
  }> => {
    const res = await client.get(`/emailUpdates/sessions/${sessionId}`);
    return res.data;
  },

  // ============================================
  // EMAIL TEMPLATES
  // ============================================

  getEmailTemplates: async (): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      name: string;
      description: string;
      subject: string;
      content: string;
      created_at: string;
      updated_at: string;
    }>;
  }> => {
    const res = await client.get("/templates");
    return res.data;
  },

  createEmailTemplate: async (template: {
    name: string;
    description: string;
    subject: string;
    content: string;
  }): Promise<{
    success: boolean;
    data: {
      id: string;
      name: string;
      description: string;
      subject: string;
      content: string;
      created_at: string;
    };
  }> => {
    const res = await client.post("/templates", template);
    return res.data;
  },

  updateEmailTemplate: async (
    templateId: string,
    template: {
      name?: string;
      description?: string;
      subject?: string;
      content?: string;
    }
  ): Promise<{
    success: boolean;
    data: any;
  }> => {
    const res = await client.put(`/templates/${templateId}`, template);
    return res.data;
  },

  deleteEmailTemplate: async (
    templateId: string
  ): Promise<{
    success: boolean;
  }> => {
    const res = await client.delete(`/templates/${templateId}`);
    return res.data;
  },

  // ============================================
  // EMAIL DRAFTS
  // ============================================

  getDrafts: async (): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      subject: string;
      content: any;
      workflow_config: any;
      selected_integrations: string[];
      created_at: string;
      updated_at: string;
    }>;
  }> => {
    const res = await client.get("/drafts");
    return res.data;
  },

  getDraft: async (
    draftId: string
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      subject: string;
      content: any;
      workflow_config: any;
      selected_integrations: string[];
      created_at: string;
      updated_at: string;
    };
  }> => {
    const res = await client.get(`/drafts/${draftId}`);
    return res.data;
  },

  createDraft: async (draft: {
    subject?: string;
    content?: any;
    workflow_config?: any;
    selected_integrations?: string[];
  }): Promise<{
    success: boolean;
    data: {
      id: string;
      subject: string;
      content: any;
      workflow_config: any;
      selected_integrations: string[];
      created_at: string;
    };
  }> => {
    const res = await client.post("/drafts", draft);
    return res.data;
  },

  updateDraft: async (
    draftId: string,
    draft: {
      subject?: string;
      content?: any;
      workflow_config?: any;
      selected_integrations?: string[];
    }
  ): Promise<{
    success: boolean;
    data: any;
  }> => {
    const res = await client.put(`/drafts/${draftId}`, draft);
    return res.data;
  },

  deleteDraft: async (
    draftId: string
  ): Promise<{
    success: boolean;
  }> => {
    const res = await client.delete(`/drafts/${draftId}`);
    return res.data;
  },

  // ============================================
  // AI FEATURES
  // ============================================

  revampEmail: async (data: {
    content: string | any;
    instructions: string;
    subject?: string;
  }): Promise<{
    success: boolean;
    data: {
      subject: string;
      content: string;
      originalInstructions: string;
    };
  }> => {
    const res = await client.post("/ai/revamp-email", data);
    return res.data;
  },

  generateSubject: async (data: {
    content: string | any;
    context?: string;
  }): Promise<{
    success: boolean;
    data: {
      subjects: string[];
    };
  }> => {
    const res = await client.post("/ai/generate-subject", data);
    return res.data;
  },

  // ============================================
  // AI EMAIL GENERATION (with documents)
  // ============================================

  generateEmail: async (data: {
    integrationIds?: string[];
    workflowId?: string;
    context?: string;
    tone?: string;
  }): Promise<{
    success: boolean;
    data: {
      subject: string;
      htmlContent: string;
      textContent: string;
    };
  }> => {
    const res = await client.post("/api/emails/generate", data);
    return res.data;
  },

  revampEmailWithDocuments: async (data: {
    content: string | any;
    subject?: string;
    instructions: string;
    additionalContext?: string;
    direction?: string;
    documents?: Array<{
      id: string;
      filename: string;
      mimeType: string;
      base64Data?: string;
      extractedText?: string;
      mediaType?: string;
    }>;
  }): Promise<{
    success: boolean;
    data: {
      subject: string;
      htmlContent: string;
      textContent: string;
      originalInstructions: string;
    };
  }> => {
    const res = await client.post("/api/emails/revamp", data);
    return res.data;
  },

  uploadDocumentsForAI: async (files: File[]): Promise<{
    success: boolean;
    data: {
      documents: Array<{
        id: string;
        filename: string;
        mimeType: string;
        size: number;
        hasImage: boolean;
        hasText: boolean;
        textPreview?: string;
      }>;
      processedDocuments: Array<{
        id: string;
        filename: string;
        mimeType: string;
        size: number;
        base64Data?: string;
        extractedText?: string;
        mediaType?: string;
      }>;
    };
  }> => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    const res = await client.post("/api/emails/upload-documents", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  },

  // ============================================
  // USER INTEGRATIONS
  // ============================================

  getUserIntegrations: async (): Promise<{
    success: boolean;
    data: Array<{
      id: string;
      provider: string;
      name: string;
      status: string;
    }>;
  }> => {
    const res = await client.get("/integrations");
    return res.data;
  },

  // ============================================
  // VC INVITES
  // ============================================

  createVCInvite: async (email: string, customMessage?: string) => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(
      `${API_BASE_URL}/api/vc-invites/admin/invites`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ email, customMessage }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create invite");
    }

    return response.json();
  },

  validateInvite: async (
    token: string
  ): Promise<{
    valid: boolean;
    email?: string;
    error?: string;
    inviteId?: string;
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/vc-invites/validate?token=${encodeURIComponent(
        token
      )}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      try {
        const error = await response.json();
        return {
          valid: false,
          error: error.error || "Failed to validate invite",
        };
      } catch {
        return {
          valid: false,
          error: "Failed to validate invite",
        };
      }
    }

    return response.json();
  },

  acceptInvite: async (inviteId: string) => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/vc-invites/accept`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ inviteId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to accept invite");
    }

    return response.json();
  },

  getAdminInvites: async () => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(
      `${API_BASE_URL}/api/vc-invites/admin/invites`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch invites");
    }

    return response.json();
  },

  // Portfolio Company Management
  createPortfolioInvite: async (
    email: string,
    companyName: string,
    customMessage?: string
  ): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/vc/portfolio/invite`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ email, companyName, customMessage }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create portfolio invite");
    }

    return response.json();
  },

  validatePortfolioInvite: async (
    token: string
  ): Promise<{
    valid: boolean;
    email?: string;
    companyName?: string;
    inviteId?: string;
    vcName?: string;
    vcEmail?: string;
    requiredCategories?: string[];
    error?: string;
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/portfolio-invites/validate?token=${encodeURIComponent(
        token
      )}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      // Try to parse JSON error, fallback to text or default message
      let errorMessage = "Failed to validate portfolio invite";
      try {
        const error = await response.json();
        errorMessage = error.error || errorMessage;
      } catch {
        // If JSON parsing fails, try to read as text
        try {
          const text = await response.text();
          if (text) {
            errorMessage = text;
          }
        } catch {
          // Use default message if both parsing methods fail
        }
      }
      return {
        valid: false,
        error: errorMessage,
      };
    }

    return response.json();
  },

  acceptPortfolioInvite: async (inviteId: string): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(
      `${API_BASE_URL}/api/portfolio-invites/accept`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ inviteId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to accept portfolio invite");
    }

    return response.json();
  },

  getVCRequiredIntegrations: async (
    inviteId: string
  ): Promise<{
    success: boolean;
    requiredIntegrations: string[];
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/api/portfolio-invites/${encodeURIComponent(
        inviteId
      )}/required-integrations`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Failed to fetch required integrations");
    }

    return response.json();
  },

  resendPortfolioInvite: async (inviteId: string): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(
      `${API_BASE_URL}/api/vc/portfolio/invites/${inviteId}/resend`,
      {
        method: "POST",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to resend portfolio invite");
    }

    return response.json();
  },

  createPortfolioRelationshipFromSlug: async (
    vcId: string,
    userId: string,
    userEmail: string
  ): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;

    const response = await fetch(
      `${API_BASE_URL}/api/vc/portfolio/relationship/from-slug`,
      {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ vcId, userId, userEmail }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to create relationship");
    }

    return response.json();
  },

  getVCInviteSlug: async (): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/vc/invite-slug`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch invite slug");
    }

    return response.json();
  },

  getVCPortfolio: async (): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/vc/portfolio/companies`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch portfolio");
    }

    return response.json();
  },

  getCompanyDetails: async (companyId: string): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(
      `${API_BASE_URL}/api/vc/portfolio/companies/${companyId}`,
      {
        method: "GET",
        headers,
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch company details");
    }

    return response.json();
  },

  getPortfolioUpdates: async (filters?: {
    companyId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
  }): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const queryParams = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const queryString = queryParams.toString();
    const url = `${API_BASE_URL}/api/vc/portfolio/updates${queryString ? `?${queryString}` : ''}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      } as Record<string, string>,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch portfolio updates");
    }

    return response.json();
  },

  getMyVCs: async (): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}/api/portfolio-companies/my-vcs`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        ...authHeaders,
      } as Record<string, string>,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch VCs");
    }

    return response.json();
  },

  // ============================================
  // ADMIN
  // ============================================

  getAdminStats: async (): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch admin stats");
    }

    return response.json();
  },

  getVCInviteDetails: async (): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/admin/vc-invites`, {
      method: "GET",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to fetch VC invite details");
    }

    return response.json();
  },

  deleteVCInvite: async (inviteId: string): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/admin/vc-invites/${inviteId}`, {
      method: "DELETE",
      headers,
      credentials: "include",
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to delete VC invite");
    }

    return response.json();
  },

  resendVCInvite: async (inviteId: string, customMessage?: string): Promise<any> => {
    const authHeaders = await getAuthHeaders();
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    } as Record<string, string>;
    const response = await fetch(`${API_BASE_URL}/api/admin/vc-invites/${inviteId}/resend`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify({ customMessage }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to resend VC invite");
    }

    return response.json();
  },
};

// Keep the default export for backward compatibility
export default client;
